import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { churchApi } from "api/api";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Switch from "@mui/material/Switch";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import CircularProgress from "@mui/material/CircularProgress";

import GlassLayout from "layouts/authentication/components/GlassLayout";

function Cover() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    address: "",
    abbreviation: "",
    gps_address: "",
    phone: "",
    has_branches: false,
    branch_name: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openSuccess, setOpenSuccess] = useState(false);
  const [openError, setOpenError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [registeredChurch, setRegisteredChurch] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = ["name", "email", "address", "abbreviation", "phone"];

    requiredFields.forEach((field) => {
      if (!formData[field]) newErrors[field] = "This field is required";
    });

    if (formData.has_branches && !formData.branch_name.trim()) {
      newErrors.branch_name = "Name your main branch (e.g. Head Office)";
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBranchToggle = (e) => {
    const checked = e.target.checked;
    setFormData((prev) => ({ ...prev, has_branches: checked }));
    if (errors.branch_name) setErrors((prev) => ({ ...prev, branch_name: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setOpenError(true);
      setErrorMessage("Please fill all required fields correctly");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await churchApi.register(formData);
      setRegisteredChurch(response.data || response);
      setOpenSuccess(true);
    } catch (error) {
      console.error("Registration error:", error);
      if (error.errors) setErrors(error.errors);
      setOpenError(true);
      setErrorMessage(error.message || "Registration failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSuccess(false);
    setOpenError(false);
  };

  const handleContinueToUserRegistration = () => {
    if (registeredChurch) {
      navigate(
        `/authentication/register/${registeredChurch.id}/${encodeURIComponent(
          registeredChurch.name
        )}`
      );
    }
  };

  return (
    <GlassLayout maxWidth="md" eyebrow="Set up your church's account in a couple of minutes.">
      <Box sx={{ p: { xs: 3, sm: 4 } }}>
        <Typography variant="h4" fontWeight={700} textAlign="center">
          Register your church
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          sx={{ mt: 0.5, mb: 3 }}
        >
          Fill in the form below
        </Typography>

        <Box component="form" role="form" onSubmit={handleSubmit}>
          <Box display="flex" gap={2} mb={2} flexWrap={{ xs: "wrap", sm: "nowrap" }}>
            <TextField
              type="text"
              name="name"
              label="Church Name *"
              fullWidth
              value={formData.name}
              onChange={handleChange}
              error={!!errors.name}
              helperText={errors.name}
            />
            <TextField
              type="email"
              name="email"
              label="Email *"
              fullWidth
              value={formData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
            />
          </Box>

          <Box display="flex" gap={2} mb={2} flexWrap={{ xs: "wrap", sm: "nowrap" }}>
            <TextField
              type="text"
              name="address"
              label="Address *"
              fullWidth
              value={formData.address}
              onChange={handleChange}
              error={!!errors.address}
              helperText={errors.address}
            />
            <TextField
              type="text"
              name="abbreviation"
              label="Abbreviation *"
              fullWidth
              value={formData.abbreviation}
              onChange={handleChange}
              error={!!errors.abbreviation}
              helperText={errors.abbreviation}
            />
          </Box>

          <Box display="flex" gap={2} mb={2} flexWrap={{ xs: "wrap", sm: "nowrap" }}>
            <TextField
              type="text"
              name="gps_address"
              label="GPS Address"
              fullWidth
              value={formData.gps_address}
              onChange={handleChange}
              error={!!errors.gps_address}
              helperText={errors.gps_address}
            />
            <TextField
              type="text"
              name="phone"
              label="Telephone *"
              fullWidth
              value={formData.phone}
              onChange={handleChange}
              error={!!errors.phone}
              helperText={errors.phone}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              ml: -1,
              mb: formData.has_branches ? 1 : 2,
              p: 1.5,
              borderRadius: 2,
              bgcolor: "rgba(0,0,0,0.03)",
            }}
          >
            <Box>
              <Typography variant="body2" fontWeight={600}>
                Does your church have multiple branches?
              </Typography>
              <Typography variant="caption" color="text.secondary">
                We&apos;ll set up your first branch so you can add more later.
              </Typography>
            </Box>
            <Switch checked={formData.has_branches} onChange={handleBranchToggle} />
          </Box>

          {formData.has_branches && (
            <Box mb={2}>
              <TextField
                type="text"
                name="branch_name"
                label="Main Branch Name *"
                placeholder="e.g. Head Office, Accra Branch"
                fullWidth
                value={formData.branch_name}
                onChange={handleChange}
                error={!!errors.branch_name}
                helperText={errors.branch_name}
              />
            </Box>
          )}

          <Box display="flex" alignItems="center" sx={{ ml: -1, mb: 2 }}>
            <Checkbox required />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ cursor: "pointer", userSelect: "none" }}
            >
              I agree to the&nbsp;
              <Typography
                component="a"
                href="#"
                variant="body2"
                color="primary"
                fontWeight={700}
                sx={{ display: "inline" }}
              >
                Terms and Conditions
              </Typography>
            </Typography>
          </Box>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{ mt: 1, mb: 1, borderRadius: 2, textTransform: "none", fontWeight: 700 }}
          >
            {isSubmitting ? "Registering..." : "Register Church"}
          </Button>

          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 2 }}>
            Already have an account?{" "}
            <Typography
              component={Link}
              to="/authentication/sign-in"
              variant="body2"
              color="primary"
              fontWeight={700}
              sx={{ textDecoration: "none", display: "inline" }}
            >
              Sign In
            </Typography>
          </Typography>
        </Box>
      </Box>

      {/* Success Notification */}
      <Snackbar
        open={openSuccess}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: "100%" }}
          action={
            <Button color="inherit" size="small" onClick={handleContinueToUserRegistration}>
              Continue
            </Button>
          }
        >
          Church registered successfully! Continue to create admin account.
        </Alert>
      </Snackbar>

      {/* Error Notification */}
      <Snackbar
        open={openError}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: "100%" }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </GlassLayout>
  );
}

export default Cover;
